'use client';

import { message } from 'antd';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import MindverseModal, { SizeType } from '@/components/modal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import useSubscription from '@/hooks/useSubscription';
import { useTranslation } from '@/i18n/client';
import { updatePlan } from '@/service/subscription';
import type { PackageDetailInfo } from '@/types/subscription';
import { EPlanAlias, EPlanInterval, PlanAliasWeightMap } from '@/types/subscription';
import { EVENT } from '@/utils/event';

import { CardItem } from './CardItem';
import styles from './index.less';

interface ITabList {
  key: string;
  title: React.ReactNode;
  children: React.ReactNode;
}

interface EPlanKey {
  alias: EPlanAlias;
  interval: EPlanInterval;
}

export const PricingCards = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenNotify, setIsOpenNotify] = useState(false);
  const [messageApi, messageContextHolder] = message.useMessage();
  const [selectingPlan, setSelectingPlan] = useState<EPlanKey>();
  const [selectingPriceId, setSelectingPriceId] = useState<string>();

  const currentPlanDetail = useSubscription((state) => state.currentPlanDetail);
  const priceList = useSubscription((state) => state.priceList);
  const { updateUsage, updatePriceList } = useSubscription((state) => ({
    updateUsage: state.updateUsage,
    updatePriceList: state.updatePriceList
  }));

  const [layoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);

  useEffect(() => {
    setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  }, [layoutSize]);

  const authIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const clearAuthInterval = () => {
    if (authIntervalRef.current) {
      clearInterval(authIntervalRef.current);
      authIntervalRef.current = null;

      dispatchEvent(new Event(EVENT.UPDATE_SUBSCRIPTION));
    }
  };

  const selectPackage = (planKey: EPlanKey, priceId: string) => {
    setSelectingPlan(planKey);
    setSelectingPriceId(priceId);

    // Free -> * 的时候直接购买即可
    if (currentPlanDetail?.currentPlanAlias === EPlanAlias.FREE) {
      upgradePackage(priceId);
    } else {
      setIsOpenNotify(true);
    }
  };

  const upgradePackage = (priceId: string) => {
    if (!currentPlanDetail) {
      return;
    }

    setIsLoading(true);
    updatePlan({
      priceId,
      successUrl: `${window.location.origin}`,
      referralId: ''
      // referralId: (window as any)?.Rewardful?.referral || ''
    })
      .then((res: any) => {
        if (res.data.code === 0) {
          if (inMobile) {
            window.location.href = res.data.data.url;

            return;
          }

          const proxy = window.open(res.data.data.url, '_blank');

          const handleWatch = () => {
            if (!proxy || proxy.closed) {
              clearAuthInterval();
              setIsLoading(false);
              updateUsage();
              updatePriceList();
            }
          };

          authIntervalRef.current = setInterval(handleWatch, 1000);
        } else {
          setIsOpenNotify(false);

          throw new Error(res.data.message ?? 'Service error');
        }
      })
      .catch((error: any) => {
        messageApi.error(error?.message);
        setIsLoading(false);
      });
  };

  const ruleTitle = (type: EPlanAlias) => {
    switch (type) {
      case EPlanAlias.FREE:
        return t('menu.subscriptionModal.freePlan');
      case EPlanAlias.PLUS:
        return t('menu.subscriptionModal.plusPlan');
      case EPlanAlias.PRO:
        return t('menu.subscriptionModal.proPlan');
    }
  };

  const ruleInterval = (type: EPlanInterval) => {
    switch (type) {
      case EPlanInterval.MONTHLY:
        return 'Monthly';
      case EPlanInterval.ANNUAL:
        return 'Annual';
    }
  };

  const renderSubscriptionTitle = (from?: EPlanKey, to?: EPlanKey) => {
    const defaultValue = 'Subscription';

    if (!from?.alias || !to?.alias) return defaultValue;

    const targetPlanTitle = ruleTitle(to.alias);

    const up = t('menu.subscriptionModal.upTitle', { targetPlanTitle: targetPlanTitle });

    const down = t('menu.subscriptionModal.downTitle', { targetPlanTitle: targetPlanTitle });

    const cancel = down;

    const transform = t('menu.subscriptionModal.transformTitle', {
      interval: ruleInterval(to.interval),
      targetPlanTitle: targetPlanTitle
    });

    if (to.alias === EPlanAlias.FREE) return cancel;

    if (PlanAliasWeightMap[from.alias] < PlanAliasWeightMap[to.alias]) return up;

    if (PlanAliasWeightMap[from.alias] > PlanAliasWeightMap[to.alias]) return down;

    if (PlanAliasWeightMap[from.alias] == PlanAliasWeightMap[to.alias]) return transform;

    return defaultValue;
  };

  const renderSubscriptionContent = (from?: EPlanKey, to?: EPlanKey) => {
    const defaultValue = 'Please confirm.';

    if (!from?.alias || !to?.alias) return defaultValue;

    const currentPlanTitle = ruleTitle(from.alias);
    const targetPlanTitle = ruleTitle(to.alias);
    const down = (
      <span
        dangerouslySetInnerHTML={{
          __html: t('menu.subscriptionModal.selectDown', {
            currentPlanTitle: currentPlanTitle,
            targetPlanTitle: targetPlanTitle
          })
        }}
      />
    );
    const up = (
      <span
        dangerouslySetInnerHTML={{
          __html: t('menu.subscriptionModal.selectUp', {
            currentPlanTitle: currentPlanTitle,
            targetPlanTitle: targetPlanTitle
          })
        }}
      />
    );

    const cancel = down;

    const transform = (
      <span>
        You&apos;re moving from{' '}
        <b>
          {ruleInterval(from.interval)} {currentPlanTitle}
        </b>{' '}
        to{' '}
        <b>
          {ruleInterval(to.interval)} {targetPlanTitle}
        </b>
        . We&apos;ll adjust the price difference—refund or charge as needed. Your new{' '}
        <b>{targetPlanTitle}</b> plan starts right away.
      </span>
    );

    if (to.alias === EPlanAlias.FREE) return cancel;

    if (PlanAliasWeightMap[from.alias] < PlanAliasWeightMap[to.alias]) return up;

    if (PlanAliasWeightMap[from.alias] > PlanAliasWeightMap[to.alias]) return down;

    if (PlanAliasWeightMap[from.alias] == PlanAliasWeightMap[to.alias]) return transform;

    return defaultValue;
  };

  const getBgText = (alias: EPlanAlias) => {
    if (alias === EPlanAlias.FREE) return t('menu.subscriptionModal.free');

    return t('menu.subscriptionModal.pro');
  };

  const renderTabTitle = (type: EPlanInterval) => {
    switch (type) {
      case EPlanInterval.MONTHLY:
        return (
          <div>
            <span className="!font-medium !text-sm !leading-6">
              {t('menu.subscriptionModal.monthly')}
            </span>
          </div>
        );
      case EPlanInterval.ANNUAL:
        return (
          <div className="flex items-center">
            <span className="!font-medium !text-sm !leading-6">
              {t('menu.subscriptionModal.annual')}
            </span>
            <span className="!font-normal  !text-xs !leading-5">
              {t('menu.subscriptionModal.annualTip')}
            </span>
          </div>
        );
    }
  };

  const renderChildren = (list: PackageDetailInfo[]) => {
    return list.map((item) => (
      <CardItem
        key={item.priceId}
        detail={item}
        isLoading={isLoading && selectingPlan?.alias === item.alias}
        onSelectPlan={() => {
          selectPackage({ alias: item.alias, interval: item.metadata.interval }, item.priceId);
        }}
        tag={getBgText(item.alias)}
        using={
          currentPlanDetail?.currentPlanAlias === item.alias &&
          currentPlanDetail.planInterval === item.metadata.interval
        }
      />
    ));
  };

  const tabList: ITabList[] = [
    {
      key: EPlanInterval.MONTHLY,
      title: renderTabTitle(EPlanInterval.MONTHLY),
      children: renderChildren(
        priceList.filter(
          (item) =>
            item.alias === EPlanAlias.FREE || item.metadata.interval === EPlanInterval.MONTHLY
        )
      )
    },
    {
      key: EPlanInterval.ANNUAL,
      title: renderTabTitle(EPlanInterval.ANNUAL),
      children: renderChildren(
        priceList.filter(
          (item) =>
            item.alias === EPlanAlias.FREE || item.metadata.interval === EPlanInterval.ANNUAL
        )
      )
    }
  ];

  return (
    <>
      <div className={classNames(styles.pricingCards, 'space-x-4')}>
        <Tabs
          className="w-full"
          defaultValue={
            currentPlanDetail?.currentPlanAlias == EPlanAlias.FREE
              ? EPlanInterval.ANNUAL
              : currentPlanDetail?.planInterval || EPlanInterval.ANNUAL
          }
        >
          <TabsList className="w-fit">
            {tabList.map((item) => (
              <TabsTrigger key={item.key} className="py-1 px-6" value={item.key}>
                {item.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabList.map((item) => (
            <TabsContent key={item.key} className="pt-6 mt-0" value={item.key}>
              <div
                className={classNames(
                  'w-full flex',
                  inMobile ? 'flex-col items-center gap-[20px]' : 'space-x-6'
                )}
              >
                {item.children}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Modal */}
      <MindverseModal
        cancelText={t('menu.subscriptionModal.cancel')}
        className={styles.modalContainer}
        destroyOnClose
        okButtonProps={{
          loading: isLoading
        }}
        okText={t('menu.subscriptionModal.confirm')}
        onCancel={() => {
          setIsOpenNotify(false);
          clearAuthInterval();
        }}
        onOk={() => {
          if (!selectingPriceId) return;

          upgradePackage(selectingPriceId);
        }}
        open={isOpenNotify}
        size={SizeType.normal}
        title={
          currentPlanDetail
            ? renderSubscriptionTitle(
                {
                  alias: currentPlanDetail.currentPlanAlias,
                  interval: currentPlanDetail.planInterval
                },
                selectingPlan
              )
            : 'Subscription'
        }
      >
        {currentPlanDetail
          ? renderSubscriptionContent(
              {
                alias: currentPlanDetail.currentPlanAlias,
                interval: currentPlanDetail.planInterval
              },
              selectingPlan
            )
          : 'Please confirm.'}
      </MindverseModal>
      {messageContextHolder}
    </>
  );
};
